import { GraphQLClient, ClientError } from 'graphql-request';
import {
  isWwsError,
  WeddingWebsiteQuery,
  WeddingWebsiteQueryDocument,
  WeddingWebsiteQueryVariables,
} from '../types/graphql';
import { IncomingMessage } from 'http';

const federationUrl = process.env['PUBLIC_FEDERATION_URL'] as string;
const apiKey = process.env['PUBLIC_FEDERATION_KEY'] as string;

// Module-level singleton so graphql-request reuses a single HTTP connection
// pool across all SSR requests instead of allocating a new GraphQLClient (and
// its underlying fetch/undici instance) on every call.
const gqlClient = new GraphQLClient(federationUrl);

// `guestWedding`/`events`/`wedding`/`weddingDatePreferences` are resolved from
// the wedding-identifier header alone (no `pin` argument exists for them), so
// they can carry real event/guest details even when `weddingWebsiteResult`
// correctly reports that a pin is required. Strip them whenever the site's
// pin gate hasn't been satisfied, regardless of what the upstream API sends.
function redactGatedFieldsIfPinRequired(
  data: WeddingWebsiteQuery
): WeddingWebsiteQuery {
  if (!isWwsError(data.weddingWebsiteResult)) {
    return data;
  }

  return {
    ...data,
    guestWedding: null,
    events: [],
    wedding: null,
    weddingDatePreferences: null,
  };
}

function errorHandler(
  error: ClientError,
  context: { slug?: string; hasToken?: boolean }
): Error {
  const messages: string[] = [];

  if (error.response?.errors) {
    error.response.errors.forEach((err) => {
      if (err.extensions?.['code'] !== 'PIN_REQUIRED') {
        messages.push(
          `Message: ${err.message} in paths ${err.path?.join(', ')}`
        );
      }
    });
  }

  const detail = messages.length > 0 ? messages.join('; ') : error.message;
  return new Error(`Slug: ${context.slug} — ${detail}`);
}

export async function makeGuestServicesAPIRequest(
  variables: WeddingWebsiteQueryVariables,
  options?: {
    sessionToken?: string;
  },
  headers?: IncomingMessage['headers']
): Promise<WeddingWebsiteQuery> {
  const slug = variables.slug;
  try {
    const requestHeaders: Record<string, string> = Object.fromEntries(
      Object.entries({
        'content-type': 'application/json',
        'x-api-key': apiKey,
        'x-wedding-identifier': encodeURIComponent(slug),
        ...(options?.sessionToken ? { token: options.sessionToken } : {}),
        // Forward only the subset of incoming headers this API explicitly allows
        ...(headers?.['xo-application']
          ? { 'xo-application': headers['xo-application'] as string }
          : {}),
        ...(headers?.['applicationversion']
          ? { applicationversion: headers['applicationversion'] as string }
          : {}),
        ...(headers?.['x-wws-api-version']
          ? { 'x-wws-api-version': headers['x-wws-api-version'] as string }
          : {}),
        ...(headers?.['x-datadome-clientid']
          ? { 'x-datadome-clientid': headers['x-datadome-clientid'] as string }
          : {}),
      }).filter(([, value]) => typeof value !== 'undefined')
    ) as Record<string, string>;

    const data = await gqlClient.request<
      WeddingWebsiteQuery,
      WeddingWebsiteQueryVariables
    >({
      document: WeddingWebsiteQueryDocument,
      variables,
      requestHeaders,
    });

    return redactGatedFieldsIfPinRequired(data);
  } catch (error) {
    const clientError = error as ClientError;
    const partialData = clientError.response?.data as
      | WeddingWebsiteQuery
      | undefined;
    if (partialData?.weddingWebsiteResult) {
      return redactGatedFieldsIfPinRequired(partialData);
    }
    throw errorHandler(clientError, {
      slug,
      hasToken: Boolean(options?.sessionToken),
    });
  }
}
